import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ReturnOrderDto, IReturnOrderDto, SiteDto, OrderGoodsItemDto, ReturnOrderDataDto } from '../../api/ClientApi';
import { RootState } from '../reducers';
import { myAssert } from '../../common/console';
import { WarehouseClient } from '../../api/Extensions';
import { filterUniqueBareGoodsByArticle } from './helper';

export const createNewEmptyReturnOrder = createAction('returnOrder/createNewEmptyReturnOrder');
export const clearReturnOrder = createAction('returnOrder/clearReturnOrder');

export const setReturnOrderOriginSite = createAction<SiteDto | undefined>('returnOrder/setReturnOrderOriginSite');
export const removeReturnOrderOriginSite = createAction('returnOrder/removeReturnOrderOriginSite');
export const updateReturnOrderDescription = createAction<string | undefined>(
    'returnOrder/updateReturnOrderDescription',
);
export const updateReturnOrderData = createAction<string | undefined>('returnOrder/updateReturnOrderData');

export const createNewReturnOrder = createAction<IReturnOrderDto>('returnOrder/createNewReturnOrder');
export const addOrUpdateReturnOrder = createAction<ReturnOrderDto>('returnOrder/addOrUpdateReturnOrder');

export const addOrderGoodsItemToReturnOrder = createAction<{
    returnOrderId: number;
    returnOrderGoodsItem?: OrderGoodsItemDto;
}>('returnOrder/addOrderGoodsItemToReturnOrder');

export const removeOrderGoodsItemFromReturnOrder = createAction<{
    returnOrderId: number;
    returnOrderGoodsItemId?: number;
}>('returnOrder/removeOrderGoodsItemFromReturnOrder');

export const saveReturnOrder = createAsyncThunk<ReturnOrderDto, void>(
    'returnOrder/saveReturnOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const returnOrderState = rootState.returnOrderForm;

        const returnOrder = returnOrderState.returnOrder;
        const bareGoods = rootState.goodsToOrderForm.bareGoodsEntities;

        myAssert(() => returnOrder !== null);

        if (returnOrder === null) {
            throw new Error();
        }

        const returnOrderId: number | undefined = returnOrder.id;
        let savedReturnOrder: ReturnOrderDto | undefined;

        const newReturnOrder = new ReturnOrderDto(returnOrder);

        const filteredBareGoods = filterUniqueBareGoodsByArticle(bareGoods ?? []);

        if (!returnOrderId || returnOrderId <= 0) {
            const addReturnOrderPromise = WarehouseClient.addNewReturnOrder(newReturnOrder as ReturnOrderDto).then(
                async (resReturnOrder) => {
                    let savedReturnOrderGoodsItems: OrderGoodsItemDto[] = [];
                    const savedReturnOrderGoodsItemsPromises: Promise<OrderGoodsItemDto>[] = [];
                    filteredBareGoods.map((bg) => {
                        const newReturnOrderGood = new OrderGoodsItemDto();
                        newReturnOrderGood.articleId = bg.article?.id;
                        newReturnOrderGood.article = bg.article;
                        newReturnOrderGood.returnOrder = resReturnOrder;
                        newReturnOrderGood.returnOrderId = resReturnOrder?.id;
                        newReturnOrderGood.quantity = bg.orderedQuantity;

                        const addReturnOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForReturnOrder(
                            resReturnOrder?.id ?? 0,
                            newReturnOrderGood,
                        );

                        savedReturnOrderGoodsItemsPromises.push(addReturnOrderGoodsItemPromise);
                    });

                    savedReturnOrderGoodsItems = await Promise.all(savedReturnOrderGoodsItemsPromises);

                    resReturnOrder.orderGoodsItems = savedReturnOrderGoodsItems;

                    return resReturnOrder;
                },
            );

            savedReturnOrder = await addReturnOrderPromise;
        } else {
            myAssert(() => newReturnOrder.id !== undefined);

            if (newReturnOrder.id === undefined) {
                throw new Error();
            }

            const filteredReturnOrderGoodsItems = newReturnOrder.orderGoodsItems;

            let savedReturnOrderGoodsItems: OrderGoodsItemDto[] = [];
            let removedGoods: number[] = [];

            const savedReturnOrderGoodsItemsPromises: Promise<OrderGoodsItemDto>[] = [];
            const removedReturnOrderGoodsItemsIdsPromises: Promise<number>[] = [];

            // go through oldGoods;
            filteredReturnOrderGoodsItems?.map((fg) => {
                // check if this goods exists in new returnOrder
                const returnOrderedGoods = filteredBareGoods?.find((bg) => bg.article?.id === fg.article?.id);

                if (!!returnOrderedGoods) {
                    // if quantity is different, update existing goods quantity
                    if (returnOrderedGoods.orderedQuantity !== fg.quantity) {
                        fg.quantity = returnOrderedGoods.orderedQuantity;

                        const addReturnOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForReturnOrder(
                            returnOrderId,
                            fg,
                        ).then((res) => {
                            thunkAPI.dispatch(
                                addOrderGoodsItemToReturnOrder({ returnOrderId, returnOrderGoodsItem: res }),
                            );
                            return res;
                        });

                        savedReturnOrderGoodsItemsPromises.push(addReturnOrderGoodsItemPromise);
                    }
                } else {
                    // this goods does not exist in new returnOrder, so we have to delete it and disconnect it from the returnOrder
                    const removedReturnOrderGoodsItemsIdsPromise = WarehouseClient.deleteOrderGoodsItem(
                        fg.id ?? 0,
                    ).then(() => {
                        thunkAPI.dispatch(
                            removeOrderGoodsItemFromReturnOrder({
                                returnOrderId: newReturnOrder.id ?? 0,
                                returnOrderGoodsItemId: fg.id,
                            }),
                        );
                        return fg.id ?? 0;
                    });
                    removedReturnOrderGoodsItemsIdsPromises.push(removedReturnOrderGoodsItemsIdsPromise);
                }
            });

            filteredBareGoods
                .filter((fbg) => !filteredReturnOrderGoodsItems?.find((fg) => fg.article?.id === fbg.article?.id))
                .map((fbg) => {
                    // in this case, goods does not exist in old returnOrder, so we have to add id and connect it to the returnOrder
                    const newReturnOrderGood = new OrderGoodsItemDto();
                    newReturnOrderGood.articleId = fbg.article?.id;
                    newReturnOrderGood.article = fbg.article;
                    newReturnOrderGood.returnOrder = savedReturnOrder;
                    newReturnOrderGood.returnOrderId = savedReturnOrder?.id;
                    newReturnOrderGood.quantity = fbg.orderedQuantity;

                    const addReturnOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForReturnOrder(
                        returnOrderId,
                        newReturnOrderGood,
                    ).then((res) => {
                        thunkAPI.dispatch(addOrderGoodsItemToReturnOrder({ returnOrderId, returnOrderGoodsItem: res }));
                        return res;
                    });

                    savedReturnOrderGoodsItemsPromises.push(addReturnOrderGoodsItemPromise);
                });

            savedReturnOrderGoodsItems = await Promise.all(savedReturnOrderGoodsItemsPromises);

            removedGoods = await Promise.all(removedReturnOrderGoodsItemsIdsPromises);

            newReturnOrder.orderGoodsItems?.push(...savedReturnOrderGoodsItems);
            newReturnOrder.orderGoodsItems?.filter((ogi) => !removedGoods.includes(ogi.id ?? 0));

            const addReturnOrderPromise = WarehouseClient.getReturnOrder(returnOrderId);

            savedReturnOrder = await addReturnOrderPromise;
        }

        return savedReturnOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const returnOrderState = (thunkAPI.getState() as RootState).returnOrderForm;

            return returnOrderState.returnOrder !== null;
        },
    },
);

export const importReturnOrders = createAsyncThunk<ReturnOrderDataDto>(
    'returnOrder/importReturnOrders',
    async (thunkAPI) => {
        return WarehouseClient.getAllReturnOrders();
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const returnOrdersState = (thunkAPI.getState() as RootState).returnOrders;

            return !returnOrdersState.returnOrdersFetchActive;
        },
    },
);

export const refreshReturnOrdersData = createAsyncThunk<void, void>(
    'returnOrder/refreshReturnOrdersData',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const returnOrdersState = rootState.returnOrders;

        return WarehouseClient.getReturnOrdersDataRefreshInfo().then((info) => {
            if (info.infoDate && info.infoDate >= returnOrdersState!.returnOrdersInfoDate!) {
                thunkAPI.dispatch(importReturnOrders());
            }
        });
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const returnOrdersState = (thunkAPI.getState() as RootState).returnOrders;

            if (returnOrdersState.returnOrdersInfoDate === null) {
                return false;
            }

            const returnOrderInfoDate: Date = new Date(returnOrdersState.returnOrdersInfoDate);
            returnOrderInfoDate.setMinutes(returnOrderInfoDate.getMinutes() + 1);

            return returnOrderInfoDate < new Date();
        },
    },
);

export const importReturnOrder = createAsyncThunk<ReturnOrderDto, number>(
    'returnOrder/importReturnOrder',
    async (payload, thunkAPI) => {
        return WarehouseClient.getReturnOrder(payload);
    },
);

export const importReturnOrderForReturnOrderDetails = createAsyncThunk<ReturnOrderDto, number>(
    'returnOrder/importReturnOrderForReturnOrderDetails',
    async (payload, thunkAPI) => {
        return WarehouseClient.getReturnOrder(payload);
    },
);

export const clearReturnOrderForReturnOrderDetails = createAction('returnOrder/clearReturnOrderForReturnOrderDetails');

export const updateReturnOrderForReturnOrderDetails = createAction<ReturnOrderDto>(
    'returnOrder/updateReturnOrderForReturnOrderDetails',
);

export const removeReturnOrderForReturnOrderDetails = createAction(
    'returnOrder/removeReturnOrderForReturnOrderDetails',
);
